import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CompaniesProvider } from '@modeso/aumico-lib-companies-fe';
import {
    AumBreadcrumbComponentItem,
    AumGlobalAction,
    IAumGlobalActionEvent,
    IPageHeader,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
} from '@modeso/aumico-lib-core-fe';
import { selectAllClosingWorkflows, WorkflowsActions, WorkflowsState } from '@modeso/aumico-lib-cpmm-fe';
import { WorkflowArea } from '@modeso/aumico-lib-cpmm-fe/enums/workflow-area.enum';
import { IUserPreferences, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';
import { UserRoles } from '../../../shared/enum/userrole.enum';
import { localizationKeys } from '../../../utils/localizationKeys';
import { environment } from './../../../../environments/environment';
@UntilDestroy()
@Component({
    selector: 'app-page-companies-list.page',
    templateUrl: './companies-list.page.html',
    styleUrls: ['./companies-list.page.scss'],
})
export class CompaniesListPage extends BasePageComponent implements OnInit {
    localizationKeys = localizationKeys;
    readonly currentLanguage = this.getCurrentLanguageWithFallBack();
    readonly defaultLanguage = environment.defaultLanguage;
    onFilterEvent = new BehaviorSubject<string>(undefined);

    public userPreferredNumberOfDocumentsPerPage: number | undefined;

    roles = [UserRoles.SUPER_ADMIN];
    constructor(
        injector: Injector,
        private readonly companiesProvider: CompaniesProvider,
        private readonly matDialog: MatDialog,
        private readonly organizationsProvider: OrganizationProvider,
        private workflowsStore: Store<WorkflowsState>,
    ) {
        super(injector);

        this.authProvider
            .getCurrentUserRoles$()
            .pipe(filter((roles) => roles != null), untilDestroyed(this))
            .subscribe((roles: string[]) => {
                this.handleCurrentUserRolesPermissions(roles);
            });
    }

    readonly breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: RouteLabels.COMPANIES_LIST,
        },
    ];
    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.COMPANIES_MAIN_NAVIGATION,
    };
    actionText: string | undefined = 'aumico_new_button';
    searchText = 'aumico_search_placeholder';

    private userPreferences: IUserPreferences | undefined;

    public organizationUsers$: Observable<INameId[]>;
    public workflows: INameId[];

    override ngOnInit() {
        super.ngOnInit();
        this.userPreferences = this.localStorageService.getUserPrefrences();
        this.userPreferredNumberOfDocumentsPerPage = this.userPreferences?.numberOfDocumentsPerPage;

        this.organizationUsers$ = this.organizationsProvider
            .getPossibleAssignees()
            .pipe(
                map((users) => {
                    const userOptions = users.map((user) => ({
                        id: user.id,
                        name: `${user.firstName} ${user.lastName}`,
                        isMe: user.isMe
                    }));

                    // Add current user to the top of the list
                    const index = userOptions.findIndex((user) => user.isMe === true);
                    if (index > -1) {
                        const [item] = userOptions.splice(index, 1); // Remove the item
                        userOptions.unshift(item); // Add it to the beginning
                    }

                    return userOptions;
                })
            );
        this.workflowsStore
            .select(selectAllClosingWorkflows)
            .pipe(untilDestroyed(this))
            .subscribe((workflows) => {
                this.workflows = workflows.map((workflow) => ({
                    id: workflow.workflowId,
                    name: workflow.name,
                }));
            });

        this.workflowsStore.dispatch(
            WorkflowsActions.loadWorkflows({ filter: { area: WorkflowArea.CLOSING_PROCESS } }),
        );
    }

    private handleCurrentUserRolesPermissions(roles: string[]): void {
        if (roles.includes('ORG_USER') || roles.includes('ORG_ADMIN')) {
            this.companiesProvider.dispatchCompaniesList();
            return;
        }

        this.openCustomerAttractionDialog();
        this.actionText = undefined;
        this.searchText = undefined;
    }

    editCompany(companyId: string) {
        this.handleRoutingToFinStatementsPage(companyId);
    }

    createCompany() {
        const event: IAumGlobalActionEvent = {
            action: AumGlobalAction.UPDATECURRENTENTITY,
            value: 'New',
        };
        this.actionHeaderService.onAction(event);
        this.handleRoutingToAddPage();
    }

    private handleRoutingToFinStatementsPage(companyId: string): void {
        this.router.navigate([
            `./${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.COMPANIES}/${companyId}/${Paths.PROJECT
            }`,
        ]);
    }
    private handleRoutingToAddPage(): void {
        this.router.navigate([
            `./${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.COMPANIES}/${Paths.ADD}`,
        ]);
    }

    doFilter(text: string) {
        this.onFilterEvent.next(text);
    }

    protected override onAction(event: IAumGlobalActionEvent) {
        if (event && event.action === AumGlobalAction.NEW) {
            this.createCompany();
        }

        if (event && event.action === AumGlobalAction.SEARCH) {
            this.onFilterEvent.next(event.value);
        }
        super.onAction(event);
    }

    private openCustomerAttractionDialog(): void {
        this.matDialog.open<ModalComponent, ModalData>(ModalComponent, {
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.DEFAULT,
                content: {
                    icon: 'aumico-logo-small',
                    text: ['aumico_reports_customer_attraction_dialog_title'],
                    descriptiveText: 'aumico_reports_customer_attraction_dialog_descriptive_text',
                    button: 'aumico_reports_customer_attraction_dialog_button_text',
                    textBelowButton: 'aumico_reports_customer_attraction_dialog_text_below_button',
                },
                backgroundColor: '#3C8ED3',
            },
        });
    }

    public onNumberOfDocumentsPerPageChanged(numberOfDocumentsPerPage: number): void {
        this.userPreferredNumberOfDocumentsPerPage = numberOfDocumentsPerPage;
        this.authProvider.dispatchUpdateUserPreferences({
            ...this.userPreferences,
            numberOfDocumentsPerPage: numberOfDocumentsPerPage,
        });
        this.localStorageService.setUserPrefrences({
            ...this.userPreferences,
            numberOfDocumentsPerPage: numberOfDocumentsPerPage,
        });
    }
}
